/**
 * A Select component to list the currently available classes
 * TODO(jangmi): Show the classes with their topic titles
 **/

import {plural, t} from "@lingui/core/macro";
import {useEffect, useState} from "react";

import {
    MultiSelect,
    OptionItem,
    type LabelsValues,
} from "@khanacademy/wonder-blocks-dropdown";
import type {
    CoachedClassroom,
    StudentList,
    UserHasChildAssignmentClassrooms,
} from "@khan/assignments-types";

interface Props {
    /** Forwarded to the MultiSelect component */
    "aria-describedby"?: string;

    /**
     * Whether the dropdown should be disabled.
     */
    disabled?: boolean;

    /**
     * An optional width for the dropdown component.
     */
    dropdownWidth?: number;

    /**
     * Whether the dropdown data is being loaded.
     */
    loading?: boolean;

    /**
     * Called when the selection has changed.
     */
    onChange: (studentListIds: Array<string>) => void;

    /**
     * The selected classes in the dropdown.
     */
    selectedValues?: Array<string>;

    /**
     * Optional array of classrooms that can be passed in to skip the query
     * TODO: Figure out a common subtype of StudentList and CoachedClassroom
     */
    studentListsOrCoachedClassrooms:
        | Array<StudentList>
        | Array<CoachedClassroom>;

    /**
     * Optional id to pair a MultiSelect with a label element.
     */
    id?: string;

    /**
     * Optional param to indicate ChildAssignmentsClassroom status of all
     * assigner's classrooms
     */
    hasChildAssignmentsClassrooms?: UserHasChildAssignmentClassrooms;
}

/**
 * Default width (in case we don't provide one via props)
 */
const SELECTOR_WIDTH = 244;

/**
 * A MultiSelect wrapper to list and select the classes available for a given
 * coach.
 */
const ClassSelector = (props: Props) => {
    const {
        disabled = false,
        dropdownWidth = SELECTOR_WIDTH,
        loading = false,
        selectedValues: propSelectedValues = [],
        studentListsOrCoachedClassrooms,
        onChange,
        id,
        hasChildAssignmentsClassrooms = "NONE",
        "aria-describedby": ariaDescribedBy,
    } = props;

    const [selectedValues, setSelectedValues] =
        useState<Array<string>>(propSelectedValues);

    useEffect(() => {
        setSelectedValues(propSelectedValues);
    }, [propSelectedValues]);

    const determineLabelCopy = (
        hasCACsStatus: UserHasChildAssignmentClassrooms,
    ): Partial<LabelsValues> => {
        if (hasCACsStatus === "ALL") {
            return {
                selectNoneLabel: t`Select no children`,
                selectAllLabel: (numClasses: number) =>
                    plural(numClasses, {
                        one: "Select # children ",
                        other: "Select all # children ",
                    }),
                noneSelected: t`No children`,
                someSelected: (numSelectedValues: number) =>
                    plural(numSelectedValues, {
                        one: "# children",
                        other: "# children",
                    }),
                allSelected: t`All children`,
            };
        }

        if (hasCACsStatus === "SOME") {
            return {
                selectNoneLabel: t`Select no classes or children`,
                selectAllLabel: (numClasses: number) =>
                    plural(numClasses, {
                        one: "Select # class and children ",
                        other: "Select all # classes and children ",
                    }),
                noneSelected: t`No classes or children`,
                someSelected: (numSelectedValues: number) =>
                    plural(numSelectedValues, {
                        one: "# class and children",
                        other: "# classes and children",
                    }),
                allSelected: t`All classes and children`,
            };
        }

        return {
            selectNoneLabel: t`Select no classes`,
            selectAllLabel: (numClasses: number) =>
                plural(numClasses, {
                    one: "Select # class ",
                    other: "Select all # classes ",
                }),
            noneSelected: t`No classes`,
            someSelected: (numSelectedValues: number) =>
                plural(numSelectedValues, {
                    one: "# class",
                    other: "# classes",
                }),
            allSelected: t`All classes`,
        };
    };

    const getClassName = (studentList: StudentList): string => {
        if (
            studentList.isChildAssignmentsClassroom &&
            !!studentList.studentKaidsAndNicknames?.length &&
            studentList.studentKaidsAndNicknames[0].coachNickname
        ) {
            return studentList.studentKaidsAndNicknames[0].coachNickname;
        }

        return studentList.name || "";
    };

    const options = studentListsOrCoachedClassrooms.map((studentList) => {
        return (
            <OptionItem
                label={getClassName(studentList)}
                value={studentList.descriptor}
                key={studentList.descriptor}
                testId="assignment-class-option"
            />
        );
    });

    // Custom MultiSelect Values
    const dropdownLabels: Partial<LabelsValues> = determineLabelCopy(
        hasChildAssignmentsClassrooms,
    );

    const handleChange = (newSelectedValues: Array<string>) => {
        setSelectedValues(newSelectedValues);
        onChange(newSelectedValues);
    };

    return (
        <MultiSelect
            aria-describedby={ariaDescribedBy}
            onChange={handleChange}
            disabled={disabled || loading}
            labels={
                dropdownLabels as TSFixMe<"TODO(WB-1598): Update `labels` to be partial">
            }
            id={id}
            selectedValues={selectedValues}
            shortcuts={true}
            style={{width: dropdownWidth}}
            testId="assignment-class-selector"
        >
            {options}
        </MultiSelect>
    );
};

export default ClassSelector;
